import { apiClient } from './client';

export interface SecretChat {
  id: string;
  initiatorId: string;
  responderId: string;
  acceptedAt: string | null;
  selfDestructSeconds: number;
  createdAt: string;
  deletedAt: string | null;
}

export interface PeerBundleResponse {
  userId: string;
  identityKey: string;
  identityDhKey: string;
  identityDhSig: string;
  signedPrekey: string;
  signedPrekeySig: string;
  signedPrekeyId: number;
  oneTimePrekey: { keyId: number; publicKey: string } | null;
}

export interface OwnBundle {
  userId: string;
  identityKey: string;
  identityDhKey: string;
  identityDhSig: string;
  signedPrekey: string;
  signedPrekeySig: string;
  signedPrekeyId: number;
  createdAt: string;
  updatedAt: string;
}

export interface SecretMessageRow {
  id: string;
  chatId: string;
  senderId: string;
  isHandshake: boolean;
  createdAt: string;
  deliveredAt: string | null;
  readAt: string | null;
  expiresAt: string | null;
  /** base64url-encoded ciphertext envelope */
  envelope: string;
}

export const secretChatsApi = {
  publishBundle: (bundle: {
    identityKey: string;
    identityDhKey: string;
    identityDhSig: string;
    signedPrekey: string;
    signedPrekeySig: string;
    signedPrekeyId: number;
  }) => apiClient.post('/api/secret-chats/keys/bundle', bundle).then((r) => r.data),

  getOwnBundle: () =>
    apiClient.get<OwnBundle | null>('/api/secret-chats/keys/bundle').then((r) => r.data),

  uploadOneTimePrekeys: (keys: { keyId: number; publicKey: string }[]) =>
    apiClient.post('/api/secret-chats/keys/onetime', { keys }).then((r) => r.data),

  countOneTimePrekeys: () =>
    apiClient.get<{ count: number }>('/api/secret-chats/keys/onetime/count').then((r) => r.data),

  fetchPeerBundle: (peerId: string) =>
    apiClient
      .get<PeerBundleResponse>(`/api/secret-chats/keys/peer/${peerId}`)
      .then((r) => r.data),

  list: () => apiClient.get<SecretChat[]>('/api/secret-chats').then((r) => r.data),

  get: (id: string) => apiClient.get<SecretChat>(`/api/secret-chats/${id}`).then((r) => r.data),

  create: (peerId: string) =>
    apiClient.post<SecretChat>('/api/secret-chats', { peerId }).then((r) => r.data),

  accept: (id: string) =>
    apiClient.post<SecretChat>(`/api/secret-chats/${id}/accept`).then((r) => r.data),

  setTimer: (id: string, seconds: number) =>
    apiClient
      .patch<SecretChat>(`/api/secret-chats/${id}/timer`, { seconds })
      .then((r) => r.data),

  delete: (id: string) =>
    apiClient.delete(`/api/secret-chats/${id}`).then(() => undefined),

  send: (id: string, envelope: string, isHandshake?: boolean) =>
    apiClient
      .post(`/api/secret-chats/${id}/messages`, { envelope, isHandshake })
      .then((r) => r.data),

  fetchMessages: (id: string, afterId?: string) =>
    apiClient
      .get<SecretMessageRow[]>(`/api/secret-chats/${id}/messages`, {
        params: afterId ? { afterId } : undefined,
      })
      .then((r) => r.data),

  markRead: (id: string, msgId: string) =>
    apiClient
      .post(`/api/secret-chats/${id}/messages/${msgId}/read`)
      .then(() => undefined),

  deleteMessage: (id: string, msgId: string) =>
    apiClient.delete(`/api/secret-chats/${id}/messages/${msgId}`).then(() => undefined),
};
